/* ============================================================
 * Inline Lucide icon registry.
 * All paths are 24×24, stroke-only, linecap/linejoin=round.
 * Source: lucide.dev (ISC license). Hand-curated subset covering
 * every icon referenced by docs/prototype/web/*.html.
 * ============================================================ */

export type IconName =
  | 'alert-circle'
  | 'align-left'
  | 'arrow-left'
  | 'arrow-right'
  | 'badge-check'
  | 'banknote'
  | 'bar-chart-3'
  | 'bell'
  | 'bold'
  | 'book-open'
  | 'briefcase'
  | 'building'
  | 'building-2'
  | 'calendar'
  | 'calendar-check'
  | 'calendar-clock'
  | 'check'
  | 'check-check'
  | 'check-circle'
  | 'chevron-down'
  | 'chevron-right'
  | 'circle-dollar-sign'
  | 'clapperboard'
  | 'clipboard-check'
  | 'clipboard-list'
  | 'clock'
  | 'coins'
  | 'crown'
  | 'download'
  | 'edit-3'
  | 'eye'
  | 'eye-off'
  | 'file'
  | 'file-archive'
  | 'file-text'
  | 'flame'
  | 'gem'
  | 'graduation-cap'
  | 'hand'
  | 'heart'
  | 'id-card'
  | 'image'
  | 'inbox'
  | 'info'
  | 'italic'
  | 'languages'
  | 'link'
  | 'list'
  | 'list-tree'
  | 'loader'
  | 'loader-2'
  | 'lock'
  | 'log-out'
  | 'maximize'
  | 'megaphone'
  | 'message-circle'
  | 'message-circle-warning'
  | 'message-square'
  | 'mic'
  | 'music'
  | 'package'
  | 'package-check'
  | 'palette'
  | 'pause'
  | 'pen-tool'
  | 'play'
  | 'play-circle'
  | 'play-square'
  | 'plus'
  | 'refresh-cw'
  | 'rocket'
  | 'rotate-ccw'
  | 'scissors'
  | 'search'
  | 'settings'
  | 'share-2'
  | 'shield'
  | 'shield-check'
  | 'shopping-bag'
  | 'smartphone'
  | 'sparkles'
  | 'star'
  | 'trash-2'
  | 'trending-down'
  | 'trending-up'
  | 'upload'
  | 'upload-cloud'
  | 'user'
  | 'user-plus'
  | 'users'
  | 'video'
  | 'wallet'
  | 'x'
  | 'zap'

interface IconDescriptor {
  /** SVG body (everything inside <svg>...</svg>). */
  body: string
}

const strokeAttrs = `fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"`

const ICONS: Record<IconName, IconDescriptor> = {
  'align-left': {
    body: `<path d="M21 10H8"/><path d="M21 6H4"/><path d="M21 14H4"/><path d="M21 18H8"/>`,
  },
  'arrow-left': {
    body: `<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>`,
  },
  'arrow-right': {
    body: `<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>`,
  },
  'alert-circle': {
    body: `<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>`,
  },
  'badge-check': {
    body: `<path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/>`,
  },
  banknote: {
    body: `<rect width="20" height="12" x="2" y="6" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/>`,
  },
  'bar-chart-3': {
    body: `<path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>`,
  },
  bell: {
    body: `<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>`,
  },
  bold: {
    body: `<path d="M14 12a4 4 0 0 0 0-8H6v8"/><path d="M15 20a4 4 0 0 0 0-8H6v8Z"/>`,
  },
  'book-open': {
    body: `<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>`,
  },
  briefcase: {
    body: `<rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>`,
  },
  building: {
    body: `<rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01"/>`,
  },
  'building-2': {
    body: `<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M2 22h20"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>`,
  },
  calendar: {
    body: `<path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/>`,
  },
  'calendar-check': {
    body: `<path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="m9 16 2 2 4-4"/>`,
  },
  'calendar-clock': {
    body: `<path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h7"/><path d="M16 19a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"/><path d="M16 15.5v2l1 1"/>`,
  },
  check: {
    body: `<path d="M20 6 9 17l-5-5"/>`,
  },
  'check-check': {
    body: `<path d="m2 12 5.4 5.4a1 1 0 0 0 1.4 0L22 4.2"/><path d="m18 12 .8.8a1 1 0 0 0 1.4 0L22 11"/>`,
  },
  'check-circle': {
    body: `<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>`,
  },
  'chevron-down': {
    body: `<path d="m6 9 6 6 6-6"/>`,
  },
  'chevron-right': {
    body: `<path d="m9 18 6-6-6-6"/>`,
  },
  'circle-dollar-sign': {
    body: `<circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 18V6"/>`,
  },
  clapperboard: {
    body: `<path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h7"/><path d="M14 9h2"/><path d="M16 9h2"/><path d="M14 5h2"/><path d="M16 5h2"/><path d="m22 14-4 4-3-3-3 3 4 4 7-7Z"/><path d="M14 9v6"/>`,
  },
  'clipboard-check': {
    body: `<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="m9 14 2 2 4-4"/>`,
  },
  'clipboard-list': {
    body: `<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/>`,
  },
  clock: {
    body: `<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>`,
  },
  coins: {
    body: `<circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.71 13.88.7.71-2.82 2.82"/>`,
  },
  crown: {
    body: `<path d="M11.562 3.146a1 1 0 0 1 1.876 0l3.198 7.762 7.83 1.382a1 1 0 0 1 .504 1.687l-5.764 5.172 1.252 8.41a1 1 0 0 1-1.498.866L12 25.032l-5.96 3.392a1 1 0 0 1-1.498-.867l1.252-8.41-5.764-5.171a1 1 0 0 1 .504-1.687l7.83-1.382Z"/>`,
  },
  download: {
    body: `<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>`,
  },
  'edit-3': {
    body: `<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/>`,
  },
  eye: {
    body: `<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>`,
  },
  'eye-off': {
    body: `<path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/>`,
  },
  file: {
    body: `<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5z"/><polyline points="14 2 14 8 20 8"/>`,
  },
  'file-archive': {
    body: `<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5z"/><polyline points="14 2 14 8 20 8"/><circle cx="11.5" cy="14.5" r="2.5"/><line x1="13.27" x2="14.98" y1="16.5" y2="19.5"/><line x1="8.5" y1="13.5" y2="14.5"/>`,
  },
  'file-text': {
    body: `<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5z"/><polyline points="14 2 14 8 20 8"/><line x1="16" x2="8" y1="13" y2="13"/><line x1="16" x2="8" y1="17" y2="17"/><line x1="10" x2="8" y1="9" y2="9"/>`,
  },
  flame: {
    body: `<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>`,
  },
  gem: {
    body: `<path d="M6 3h12l4 6-10 13L2 9Z"/><path d="M11 3 8 9l4 13 4-13-3-6"/><path d="M2 9h20"/>`,
  },
  'graduation-cap': {
    body: `<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/><path d="M22 10v6"/><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/>`,
  },
  hand: {
    body: `<path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2"/><path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2"/><path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>`,
  },
  heart: {
    body: `<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>`,
  },
  'id-card': {
    body: `<path d="M16 10h2"/><path d="M16 14h2"/><path d="M6.17 15a3 3 0 0 1 5.66 0"/><circle cx="9" cy="11" r="2"/><rect x="2" y="5" width="20" height="14" rx="2"/>`,
  },
  image: {
    body: `<rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>`,
  },
  inbox: {
    body: `<polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>`,
  },
  info: {
    body: `<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>`,
  },
  italic: {
    body: `<line x1="19" x2="10" y1="4" y2="4"/><line x1="14" x2="5" y1="20" y2="20"/><line x1="15" x2="9" y1="4" y2="20"/>`,
  },
  languages: {
    body: `<path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/>`,
  },
  link: {
    body: `<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>`,
  },
  list: {
    body: `<line x1="8" x2="21" y1="6" y2="6"/><line x1="8" x2="21" y1="12" y2="12"/><line x1="8" x2="21" y1="18" y2="18"/><line x1="3" x2="3.01" y1="6" y2="6"/><line x1="3" x2="3.01" y1="12" y2="12"/><line x1="3" x2="3.01" y1="18" y2="18"/>`,
  },
  'list-tree': {
    body: `<path d="M3 5h14"/><path d="M3 12h14"/><path d="M3 19h14"/><path d="m15 9 3-3"/><path d="m18 6 3 3"/><path d="m15 16 3-3"/><path d="m18 13 3 3"/>`,
  },
  loader: {
    body: `<path d="M12 2v4"/><path d="m16.2 7.8 2.9-2.9"/><path d="M18 12h4"/><path d="m16.2 16.2 2.9 2.9"/><path d="M12 18v4"/><path d="m4.9 19.1 2.9-2.9"/><path d="M2 12h4"/><path d="m4.9 4.9 2.9 2.9"/>`,
  },
  'loader-2': {
    body: `<path d="M21 12a9 9 0 1 1-6.219-8.56"/>`,
  },
  lock: {
    body: `<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>`,
  },
  'log-out': {
    body: `<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/>`,
  },
  maximize: {
    body: `<path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>`,
  },
  megaphone: {
    body: `<path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>`,
  },
  'message-circle': {
    body: `<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z"/>`,
  },
  'message-circle-warning': {
    body: `<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z"/><path d="M12 8v4"/><path d="M12 16h.01"/>`,
  },
  'message-square': {
    body: `<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>`,
  },
  mic: {
    body: `<rect x="9" y="2" width="6" height="13" rx="3"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>`,
  },
  music: {
    body: `<path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>`,
  },
  package: {
    body: `<path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><path d="m3.3 7 7.703 4.734a2 2 0 0 0 2.007 0L20.7 7"/><path d="m7.5 4.27 9 5.15"/>`,
  },
  'package-check': {
    body: `<path d="m16 16 2 2 4-4"/><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"/><path d="M16.5 9.5 12 12 7.5 9.5"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>`,
  },
  palette: {
    body: `<circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125 0-.939.726-1.688 1.667-1.688h1.875c2.857 0 5.187-2.331 5.187-5.187C21.502 5.831 17.198 2 12 2Z"/>`,
  },
  pause: {
    body: `<rect x="14" y="4" width="4" height="16" rx="1"/><rect x="6" y="4" width="4" height="16" rx="1"/>`,
  },
  'pen-tool': {
    body: `<path d="M15.707 21.293a1 1 0 0 0 1.414 0l4.586-4.586a1 1 0 0 0 0-1.414l-1.586-1.586a1 1 0 0 0-1.414 0L12 17.586l-6.293 6.293a1 1 0 0 0 0 1.414l1.586 1.586a1 1 0 0 0 1.414 0Z"/><path d="m18 13-1.7-1.7a1 1 0 0 0-.707-.293H10l-3.293-3.293a1 1 0 0 1 0-1.414l8.586-8.586a1 1 0 0 1 1.414 0L20 5.5"/><path d="m17 8 4 4"/><path d="m21 10-4.5 4.5"/><path d="m3 21 3.6-3.6"/>`,
  },
  play: {
    body: `<polygon points="6 3 20 12 6 21 6 3"/>`,
  },
  'play-circle': {
    body: `<circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>`,
  },
  'play-square': {
    body: `<rect width="18" height="18" x="3" y="3" rx="2"/><path d="M9 9.003v6l5.5-3z"/>`,
  },
  plus: {
    body: `<path d="M5 12h14"/><path d="M12 5v14"/>`,
  },
  'refresh-cw': {
    body: `<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/>`,
  },
  rocket: {
    body: `<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>`,
  },
  'rotate-ccw': {
    body: `<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>`,
  },
  scissors: {
    body: `<circle cx="6" cy="6" r="3"/><path d="M8.12 8.12 12 12"/><path d="M20 4 8.12 15.88"/><circle cx="6" cy="18" r="3"/><path d="M14.8 14.8 20 20"/>`,
  },
  search: {
    body: `<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>`,
  },
  settings: {
    body: `<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>`,
  },
  'share-2': {
    body: `<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>`,
  },
  shield: {
    body: `<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>`,
  },
  'shield-check': {
    body: `<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>`,
  },
  'shopping-bag': {
    body: `<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>`,
  },
  smartphone: {
    body: `<rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>`,
  },
  sparkles: {
    body: `<path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>`,
  },
  star: {
    body: `<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>`,
  },
  'trash-2': {
    body: `<path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>`,
  },
  'trending-down': {
    body: `<polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/>`,
  },
  'trending-up': {
    body: `<polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>`,
  },
  upload: {
    body: `<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/>`,
  },
  'upload-cloud': {
    body: `<path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"/><path d="M12 12v9"/><path d="m16 16-4-4-4 4"/>`,
  },
  user: {
    body: `<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>`,
  },
  'user-plus': {
    body: `<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/>`,
  },
  users: {
    body: `<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>`,
  },
  video: {
    body: `<path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/>`,
  },
  wallet: {
    body: `<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 1 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/>`,
  },
  x: {
    body: `<path d="M18 6 6 18"/><path d="m6 6 12 12"/>`,
  },
  zap: {
    body: `<path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/>`,
  },
}

export function getIconBody(name: IconName): string {
  return ICONS[name]?.body ?? ''
}

export function hasIcon(name: string): name is IconName {
  return name in ICONS
}

export const ICON_LIST: IconName[] = Object.keys(ICONS) as IconName[]

export const ICON_VIEWBOX = '0 0 24 24'
export const ICON_STROKE_ATTRS = strokeAttrs

/* ============================================================
 * Brand icon registry.
 * Self-contained SVG bodies (already include their own fill colors).
 * Rendered WITHOUT the Lucide stroke wrapper — see IconSvgBody.vue.
 * ============================================================ */

export type BrandIconName = 'wechat' | 'alipay'

interface BrandIconDescriptor {
  /** SVG body (already self-contained: includes fill colors). NOT wrapped by lucide stroke attrs. */
  body: string
}

const BRAND_ICONS: Record<BrandIconName, BrandIconDescriptor> = {
  /* Simple Icons — WeChat (CC0). https://github.com/simple-icons/simple-icons */
  wechat: {
    body: `<path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z" fill="#07c160"/>`,
  },
  /* Simple Icons — Alipay (CC0). https://github.com/simple-icons/simple-icons */
  alipay: {
    body: `<path d="M19.695 15.07c3.426 1.158 4.203 1.22 4.203 1.22V3.846c0-2.124-1.705-3.845-3.81-3.845H3.914C1.808.001.102 1.722.102 3.846v16.31c0 2.123 1.706 3.845 3.813 3.845h16.173c2.105 0 3.81-1.722 3.81-3.845v-.157s-6.19-2.602-9.315-4.119c-2.096 2.602-4.8 4.181-7.607 4.181-4.75 0-6.361-4.19-4.112-6.949.49-.602 1.324-1.175 2.617-1.497 2.025-.502 5.247.313 8.266 1.317a16.796 16.796 0 0 0 1.341-3.302H5.781v-.952h4.799V6.975H4.77v-.953h5.81V3.591s0-.409.411-.409h2.347v2.84h5.744v.951h-5.744v1.704h4.69a19.453 19.453 0 0 1-1.986 5.06c1.424.52 2.702 1.011 3.654 1.333m-13.81-2.032c-.596.06-1.71.325-2.321.869-1.83 1.608-.735 4.55 2.968 4.55 2.151 0 4.301-1.388 5.99-3.61-2.403-1.182-4.438-2.028-6.637-1.809" fill="#1677ff"/>`,
  },
}

export function getBrandIconBody(name: BrandIconName): string {
  return BRAND_ICONS[name]?.body ?? ''
}

export function hasBrandIcon(name: string): name is BrandIconName {
  return name in BRAND_ICONS
}

export const BRAND_ICON_LIST: BrandIconName[] = Object.keys(BRAND_ICONS) as BrandIconName[]
